<template>
  <div class="pt-panel irrigation-info-panel">
    <div class="pt-header">
      <div class="pt-header-text">
        <div class="pt-kicker">工程信息</div>
        <h2 class="pt-title">{{ oneTitle }}</h2>
        <p class="pt-subtitle">灌区基本信息 · 供需水方案 · 实时监测</p>
      </div>
      <button class="pt-close" @click="closeClick">&times;</button>
    </div>

    <div class="pt-tabs" v-if="tabs.length > 1">
      <div
          v-for="(tab, idx) in tabs"
          :key="idx"
          class="pt-tab"
          :class="{ active: activeTab === tab }"
          @click="activeTab = tab"
      >
        {{ tab }}
      </div>
    </div>

    <!-- 基本信息 -->
    <div v-if="activeTab === '基本信息'" class="pt-flex-table pt-scroll">
      <div class="pt-flex-table-header">
        <div class="pt-flex-table-cell">项目</div>
        <div class="pt-flex-table-cell">内容</div>
      </div>
      <div
          class="pt-flex-table-row"
          v-for="(item, index) in parkInfo"
          :key="index"
      >
        <div class="pt-flex-table-cell">{{ item.label }}</div>
        <div class="pt-flex-table-cell">{{ item.value }}</div>
      </div>
    </div>

    <!-- 实时监测 -->
    <div v-if="activeTab === '实时监测'" class="ts-section">
      <div class="ts-metrics-row">
        <div class="ts-metric-card" v-for="(m, i) in tsMetrics" :key="'m'+i">
          <span class="ts-metric-label">{{ m.label }}</span>
          <div class="ts-metric-val-row">
            <span class="ts-metric-value" :class="m.cls">{{ m.value }}</span>
            <span class="ts-metric-unit">{{ m.unit }}</span>
          </div>
        </div>
      </div>

      <div class="ts-chart-group">
        <div class="ts-chart-card" v-for="(c, ci) in chartConfigs" :key="'c'+ci">
          <div class="ts-chart-title">{{ c.title }}</div>
          <div class="ts-chart-canvas" :ref="'chart' + ci"></div>
        </div>
      </div>
    </div>

    <!-- 应急方案 -->
    <div v-if="activeTab === '应急方案'" class="pt-text-box pt-scroll">
      <h3>灌区供需水不平衡应急方案</h3>
      <p>
        <strong>1. 启动条件：</strong>
        当主水源供水保证率低于 80% 或连续旱季无降水超过 15 天时，启动应急预案。
      </p>
      <p>
        <strong>2. 责任单位与指挥体系：</strong>
        由灌区管理单位成立"应急供水指挥组"，组长为灌区主管领导，成员涵盖水库调度、泵站运行、渠道管理、农业灌溉、用水户代表等。
      </p>
      <p>
        <strong>3. 备用水源启动：</strong>
        启用二级备用水源（如临近调蓄水库释放库容 10 万 m³）；若水源仍不足，联动上游调蓄设施，优先保障重点供水区。
      </p>
      <p>
        <strong>4. 分片限灌措施：</strong>
        按"先保障经济作物、后保障粮食作物、再保障生活用水"的顺序分片限灌；北部果园区优先保障，南部旱作区执行 50% 灌溉强度。
      </p>
      <p>
        <strong>5. 水量调度与监控：</strong>
        通过远程监测系统动态监控渠道流量和泵站运行，每 24 小时调度一次，必要时缩短至 12 小时；确保调度指令实时更新。
      </p>
      <p>
        <strong>6. 协调与宣传：</strong>
        发布用水预警，明确节水要求和优先供水区域；组织应急演练，确保农户知晓应急措施和灌溉时间。
      </p>
      <p>
        <strong>7. 恢复机制：</strong>
        当主水源保证率恢复至 95% 以上、备用水源关闭 7 日后，逐步解除限灌，恢复正常灌溉秩序。
      </p>
      <div style="text-align: right; margin-top: 12px;">
        <button class="pt-btn" @click="">发布</button>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { generateIrrigationTimeSeries } from '@/utils/reservoirTimeSeries.js'

export default {
  name: "irrigationInfo",
  props: {
    info: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      oneTitle: "",
      tabs: ["基本信息", "实时监测"],
      activeTab: "基本信息",
      parkInfo: [],
      tsData: null,
      tsMetrics: [],
      chartConfigs: [],
    };
  },

  created() {
    this.echartInstances = [];
  },

  mounted() {
    this.updateData();
    this._resizeHandler = () => {
      this.echartInstances.forEach(c => { try { c.resize(); } catch(e) {} });
    };
    window.addEventListener('resize', this._resizeHandler);
  },

  watch: {
    info: {
      handler(val) {
        this.oneTitle = val.name || "灌区信息";
        this.updateData();
      },
      deep: true,
      immediate: true,
    },

    activeTab(val) {
      if (val === '实时监测') {
        this.$nextTick(() => {
          setTimeout(() => this._renderCharts(), 200);
        });
      }
    },
  },

  beforeDestroy() {
    this._destroyCharts();
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
  },

  methods: {
    updateData() {
      this.parkInfo = [
        { label: "灌区名称", value: this.oneTitle || "喜家岭灌区" },
        { label: "行政区划", value: "陕西省渭南市富平县" },
        { label: "设计灌溉面积", value: "2.1 万亩" },
        { label: "有效灌溉面积", value: "1.8 万亩" },
        { label: "主要水源", value: "喜家岭调蓄水库" },
        { label: "配套泵站数量", value: "3 座" },
        { label: "干渠长度", value: "12.4 km" },
        { label: "支渠数量", value: "18 条" },
        { label: "设计供水流量", value: "1.6 m³/s" },
        { label: "主要作物", value: "小麦、玉米、果树、蔬菜" },
        { label: "管理单位", value: "富平县水利局" },
      ];

      // 生成时间序列数据
      const dailyDemand = 15;
      this.tsData = generateIrrigationTimeSeries(this.oneTitle, dailyDemand);

      // 实时指标
      const ts = this.tsData;
      const lastIdx = ts.times.length - 1;
      const curDemand = ts.demand[lastIdx];
      const curSupply = ts.supply[lastIdx];
      const curDeficit = ts.deficit[lastIdx];
      const totalDemand = ts.demand.reduce((a, b) => a + b, 0);
      const totalSupply = ts.supply.reduce((a, b) => a + b, 0);
      const guaranteeRate = Math.round((totalSupply / totalDemand) * 100);

      this.tsMetrics = [
        { label: '当前需水', value: curDemand.toFixed(2), unit: '万 m³', cls: '' },
        { label: '当前供水', value: curSupply.toFixed(2), unit: '万 m³', cls: 'st-green' },
        { label: '当前缺口', value: curDeficit.toFixed(2), unit: '万 m³', cls: curDeficit > 0.1 ? 'st-red' : 'st-green' },
        { label: '供水保证率', value: guaranteeRate + '%', unit: '', cls: guaranteeRate >= 85 ? 'st-green' : 'st-yellow' },
      ];

      // 图表配置
      this.chartConfigs = [
        { title: '需水 / 供水曲线 (万 m³)', data: ts.demand, data2: ts.supply, color: '#38bdf8', color2: '#34d399', name1: '需水', name2: '供水' },
        { title: '供需缺口 (万 m³)', data: ts.deficit, color: '#fbbf24', area: true },
      ];

      if (this.info.showEmergency) {
        this.tabs = ["基本信息", "实时监测", "应急方案"];
      } else {
        this.tabs = ["基本信息", "实时监测"];
      }

      // 如果当前在实时监测标签，重新渲染图表
      if (this.activeTab === '实时监测') {
        this.$nextTick(() => {
          setTimeout(() => this._renderCharts(), 200);
        });
      }
    },

    _renderCharts() {
      this._destroyCharts();
      if (!this.tsData || !this.chartConfigs.length) return;

      this.chartConfigs.forEach((cfg, idx) => {
        const refKey = 'chart' + idx;
        const el = this.$refs[refKey];
        if (!el || (Array.isArray(el) && el.length === 0)) return;
        const dom = Array.isArray(el) ? el[0] : el;
        const w = dom.offsetWidth || dom.clientWidth || 380;
        const h = 140;
        dom.style.width = w + 'px';
        dom.style.height = h + 'px';

        let chart;
        try {
          chart = echarts.init(dom, null, { width: w, height: h, renderer: 'canvas' });
        } catch(e) {
          console.error('[irrigationInfo] echarts.init error', idx, e);
          return;
        }
        this.echartInstances.push(chart);

        const series = [{
          name: cfg.name1 || cfg.title,
          type: 'line',
          data: cfg.data,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2, color: cfg.color },
          itemStyle: { color: cfg.color },
          areaStyle: cfg.area ? {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: cfg.color + '40' },
              { offset: 1, color: cfg.color + '05' },
            ]),
          } : undefined,
        }];

        if (cfg.data2) {
          series.push({
            name: cfg.name2,
            type: 'line',
            data: cfg.data2,
            smooth: true,
            symbol: 'none',
            lineStyle: { width: 2, color: cfg.color2 },
            itemStyle: { color: cfg.color2 },
          });
        }

        chart.setOption({
          grid: { left: 50, right: 15, top: 28, bottom: 25 },
          tooltip: { trigger: 'axis', textStyle: { fontSize: 11 } },
          legend: cfg.data2 ? {
            show: true, data: [cfg.name1, cfg.name2],
            textStyle: { color: '#cbd5e1', fontSize: 10 }, top: 0, right: 5,
          } : { show: false },
          xAxis: {
            type: 'category',
            data: this.tsData.times,
            axisLabel: { color: '#94a3b8', fontSize: 9, interval: 23 },
            axisLine: { lineStyle: { color: 'rgba(148,163,184,0.3)' } },
          },
          yAxis: {
            type: 'value',
            scale: true,
            axisLabel: { color: '#94a3b8', fontSize: 9 },
            splitLine: { lineStyle: { color: 'rgba(148,163,184,0.1)' } },
          },
          series,
        });
        setTimeout(() => { try { chart.resize(); } catch(e) {} }, 100);
      });
    },

    _destroyCharts() {
      this.echartInstances.forEach(c => { try { c.dispose(); } catch(e) {} });
      this.echartInstances = [];
    },

    closeClick() {
      this.$emit("infoClose");
    },
  },
};
</script>

<style scoped>
.irrigation-info-panel {
  width: 45%;
  height: 65%;
  position: absolute;
  overflow-y: auto;
  max-height: 65%;
  left: 30%;
  top: 200px;
}

/* === 实时监测区域 === */
.ts-section {
  padding: 0 2px;
}

.ts-metrics-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.ts-metric-card {
  position: relative;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
  min-width: 100px;
}

.ts-metric-label {
  display: block;
  color: rgba(255, 255, 255, 0.52);
  font-size: 11px;
  margin-bottom: 4px;
}

.ts-metric-val-row {
  display: flex;
  align-items: baseline;
  gap: 3px;
}

.ts-metric-value {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}

.ts-metric-unit {
  color: rgba(125, 211, 252, 0.76);
  font-size: 10px;
}

.ts-chart-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ts-chart-card {
  padding: 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.ts-chart-title {
  color: rgba(125, 211, 252, 0.76);
  font-size: 11px;
  margin-bottom: 4px;
}

.ts-chart-canvas {
  width: 100%;
  height: 140px;
  min-width: 300px;
  min-height: 140px;
}

/* === 状态颜色 === */
.st-red { color: #fca5a5; }
.st-yellow { color: #facc15; }
.st-green { color: #86efac; }
</style>
